import React, { useEffect, useState } from "react";
import { DashboardStatsResponse } from "../../types/admin";
import { visitorsApi } from "../../services/api";
import { useToast } from "./Toast";

const DashboardPage: React.FC = () => {
  const { showToast } = useToast();
  const [stats, setStats] = useState<DashboardStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const openLocationOnMaps = (city?: string | null, country?: string | null, ipAddress?: string | null) => {
    const parts = [city, country].filter((value): value is string => Boolean(value && value.trim()));
    const query = parts.length > 0 ? parts.join(", ") : ipAddress || "";

    const url = query
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
      : "https://www.google.com/maps";

    window.open(url, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      try {
        const response = await visitorsApi.dashboard({
          daysForDaily: 30,
          topCountries: 10,
          topCities: 10,
        });

        if (response.success && response.data) {
          setStats(response.data);
        } else {
          showToast(response.message || "Failed to load dashboard", "error");
        }
      } catch {
        showToast("Failed to load dashboard", "error");
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="admin-loader">
        <div className="admin-spinner" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="admin-empty-state">
        <i className="fa-solid fa-chart-column" />
        <p>No dashboard data available</p>
      </div>
    );
  }

  const visitsByCountry = stats.visitsByCountry ?? [];
  const visitsByCity = stats.visitsByCity ?? [];
  const visitsByBrowser = stats.visitsByBrowser ?? [];
  const visitsByOs = stats.visitsByOs ?? [];
  const visitsByDevice = stats.visitsByDevice ?? [];
  const visitsPerDay = stats.visitsPerDay ?? [];
  const visitsByHour = stats.visitsByHour ?? [];
  const visitsByIp = stats.visitsByIp ?? [];

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Visitors Dashboard</h1>
        <p className="admin-page-subtitle">Overview of visits, location, browser and device metrics</p>
      </div>

      <div className="admin-dashboard-stats-grid">
        <div className="admin-card admin-dashboard-stat-card">
          <span className="admin-dashboard-stat-label">Total Visits</span>
          <strong className="admin-dashboard-stat-value">{stats.totalVisits}</strong>
        </div>
        <div className="admin-card admin-dashboard-stat-card">
          <span className="admin-dashboard-stat-label">Today</span>
          <strong className="admin-dashboard-stat-value">{stats.todayVisits}</strong>
        </div>
        <div className="admin-card admin-dashboard-stat-card">
          <span className="admin-dashboard-stat-label">This Week</span>
          <strong className="admin-dashboard-stat-value">{stats.thisWeekVisits}</strong>
        </div>
        <div className="admin-card admin-dashboard-stat-card">
          <span className="admin-dashboard-stat-label">This Month</span>
          <strong className="admin-dashboard-stat-value">{stats.thisMonthVisits}</strong>
        </div>
        <div className="admin-card admin-dashboard-stat-card">
          <span className="admin-dashboard-stat-label">Unique Visitors</span>
          <strong className="admin-dashboard-stat-value">{stats.uniqueVisitors}</strong>
        </div>
      </div>

      <div className="admin-dashboard-grid-2">
        <div className="admin-card">
          <div className="admin-card-header">
            <span className="admin-card-title">Top Countries</span>
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Country</th>
                  <th>Code</th>
                  <th>Visits</th>
                </tr>
              </thead>
              <tbody>
                {visitsByCountry.map((item, index) => (
                  <tr key={`${item.country || "unknown"}-${index}`}>
                    <td>{item.country || "Unknown"}</td>
                    <td>{item.countryCode || "-"}</td>
                    <td>{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-card-header">
            <span className="admin-card-title">Top Cities</span>
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>City</th>
                  <th>Country</th>
                  <th>Visits</th>
                </tr>
              </thead>
              <tbody>
                {visitsByCity.map((item, index) => (
                  <tr key={`${item.city || "unknown"}-${index}`}>
                    <td>{item.city || "Unknown"}</td>
                    <td>{item.country || "Unknown"}</td>
                    <td>{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="admin-dashboard-grid-3">
        <div className="admin-card">
          <div className="admin-card-header">
            <span className="admin-card-title">Browsers</span>
          </div>
          <div className="admin-dashboard-list">
            {visitsByBrowser.map((item, index) => (
              <div className="admin-dashboard-list-item" key={`${item.browser || "unknown"}-${index}`}>
                <span>{item.browser || "Unknown"}</span>
                <strong>{item.count}</strong>
              </div>
            ))}
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-card-header">
            <span className="admin-card-title">Operating Systems</span>
          </div>
          <div className="admin-dashboard-list">
            {visitsByOs.map((item, index) => (
              <div className="admin-dashboard-list-item" key={`${item.operatingSystem || "unknown"}-${index}`}>
                <span>{item.operatingSystem || "Unknown"}</span>
                <strong>{item.count}</strong>
              </div>
            ))}
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-card-header">
            <span className="admin-card-title">Devices</span>
          </div>
          <div className="admin-dashboard-list">
            {visitsByDevice.map((item, index) => (
              <div className="admin-dashboard-list-item" key={`${item.device || "unknown"}-${index}`}>
                <span>{item.device || "Unknown"}</span>
                <strong>{item.count}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="admin-dashboard-grid-2">
        <div className="admin-card">
          <div className="admin-card-header">
            <span className="admin-card-title">IPs That Visited</span>
          </div>
          {visitsByIp.length === 0 ? (
            <div className="admin-empty-state" style={{ minHeight: "14rem" }}>
              <i className="fa-solid fa-network-wired" />
              <p>No IP data available</p>
            </div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>IP</th>
                    <th>Location</th>
                    <th>Visits</th>
                    <th>Map</th>
                  </tr>
                </thead>
                <tbody>
                  {visitsByIp.map((item, index) => {
                    const locationLabel = [item.city, item.country]
                      .filter((value): value is string => Boolean(value && value.trim()))
                      .join(", ");

                    return (
                      <tr key={`${item.ipAddress || "unknown"}-${index}`}>
                        <td>{item.ipAddress || "Unknown"}</td>
                        <td>{locationLabel || "Unknown"}</td>
                        <td>{item.count}</td>
                        <td>
                          <button
                            className="admin-btn admin-btn-secondary admin-btn-sm"
                            onClick={() => openLocationOnMaps(item.city, item.country, item.ipAddress)}
                          >
                            <i className="fa-solid fa-location-dot" />
                            <span>Map</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="admin-card">
          <div className="admin-card-header">
            <span className="admin-card-title">Visits per Day</span>
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Visits</th>
                </tr>
              </thead>
              <tbody>
                {visitsPerDay.map((item) => (
                  <tr key={item.date}>
                    <td>{new Date(item.date).toLocaleDateString()}</td>
                    <td>{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-card-header">
            <span className="admin-card-title">Visits by Hour</span>
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Hour</th>
                  <th>Visits</th>
                </tr>
              </thead>
              <tbody>
                {visitsByHour.map((item) => (
                  <tr key={item.hour}>
                    <td>{String(item.hour).padStart(2, "0")}:00</td>
                    <td>{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
